"use client";

import React, { useEffect, useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Terminal, Loader, ChevronRight, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
export default function SecureYourStrategy() {
  return <section data-section-id="13472" id="start-your-recovery" className="dark bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full pointer-events-none opacity-20">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto">
        <div className="mb-12 text-center space-y-4">
          <div className="inline-flex items-center gap-2 text-primary/80 text-sm font-sans tracking-widest uppercase mb-2">
            <Activity className="w-4 h-4" />
            <span>Consultation Request</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white">
            Start Your <span className="text-primary">Recovery</span>
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Take the first step towards restorative sleep. During your consultation, we will review your symptoms and verify insurance coverage.
          </p>
        </div>

        <div className="bg-card/30 backdrop-blur-md border border-white/10 rounded-xl p-8 md:p-12 shadow-2xl relative overflow-hidden group">
          {}
          <div className="absolute inset-0 border border-primary/20 rounded-xl pointer-events-none" />
          <div className="absolute -inset-px bg-gradient-to-b from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none rounded-xl" />

          <FormProvider formKey="start_recovery_form_home" formId="start_recovery_form_home" sectionName="start-your-recovery" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          company_name: "",
          brief_brief: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email_address: {
            required: true,
            pattern: "email"
          },
          phone_number: {
            required: true
          },
          company_name: {
            required: true
          },
          brief_brief: {
            required: false
          }
        }} submitText="Secure Your Free Consultation" successMessage="Request received. Our sleep specialist will contact you shortly.">
            <StrategyForm />
          </FormProvider>
        </div>
      </div>
      <Toaster position="bottom-right" />
    </section>;
}
function StrategyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage, {
        className: "bg-background border-primary/30 text-foreground",
        icon: <Activity className="w-4 h-4 text-primary animate-pulse" />
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  useEffect(() => {
    if (submitError) {
      toast.error(submitError, {
        className: "bg-destructive/10 border-destructive/30 text-destructive"
      });
    }
  }, [submitError]);
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-8">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Patient name is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input {...field} placeholder=" " className="peer h-12 border-0 border-b border-white/20 bg-transparent px-0 text-base text-white placeholder:text-transparent focus-visible:border-primary focus-visible:ring-0 rounded-none transition-colors" />
              </FormControl>
              <FormLabel className="absolute left-0 top-3 text-muted-foreground duration-300 transform -translate-y-0 scale-100 origin-[0] peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:text-primary peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-75 cursor-text">
                Full Name <span className="text-primary">*</span>
              </FormLabel>
              <FormMessage className="absolute -bottom-5 left-0 text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="email_address" rules={{
        required: "Email address required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input {...field} type="email" placeholder=" " className="peer h-12 border-0 border-b border-white/20 bg-transparent px-0 text-base text-white placeholder:text-transparent focus-visible:border-primary focus-visible:ring-0 rounded-none transition-colors" />
              </FormControl>
              <FormLabel className="absolute left-0 top-3 text-muted-foreground duration-300 transform -translate-y-0 scale-100 origin-[0] peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:text-primary peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-75 cursor-text">
                Email Address <span className="text-primary">*</span>
              </FormLabel>
              <FormMessage className="absolute -bottom-5 left-0 text-xs" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {}
          <FormField control={control} name="phone_number" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem className="relative space-y-0">
                <FormControl>
                  <Input {...field} type="tel" placeholder=" " className="peer h-12 border-0 border-b border-white/20 bg-transparent px-0 text-base text-white placeholder:text-transparent focus-visible:border-primary focus-visible:ring-0 rounded-none transition-colors" />
                </FormControl>
                <FormLabel className="absolute left-0 top-3 text-muted-foreground duration-300 transform -translate-y-0 scale-100 origin-[0] peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:text-primary peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-75 cursor-text">
                  Phone Number <span className="text-primary">*</span>
                </FormLabel>
                <FormMessage className="absolute -bottom-5 left-0 text-xs" />
              </FormItem>} />

          {}
          <FormField control={control} name="company_name" rules={{
          required: "Insurance provider required"
        }} render={({
          field
        }) => <FormItem className="relative space-y-0">
                <FormControl>
                  <Input {...field} placeholder=" " className="peer h-12 border-0 border-b border-white/20 bg-transparent px-0 text-base text-white placeholder:text-transparent focus-visible:border-primary focus-visible:ring-0 rounded-none transition-colors" />
                </FormControl>
                <FormLabel className="absolute left-0 top-3 text-muted-foreground duration-300 transform -translate-y-0 scale-100 origin-[0] peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:text-primary peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-75 cursor-text">
                  Insurance Provider <span className="text-primary">*</span>
                </FormLabel>
                <FormMessage className="absolute -bottom-5 left-0 text-xs" />
              </FormItem>} />
        </div>

        {}
        <FormField control={control} name="brief_brief" render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <textarea {...field} placeholder=" " className="peer flex min-h-[100px] w-full border-0 border-b border-white/20 bg-transparent px-0 py-2 text-base text-white placeholder:text-transparent focus-visible:border-primary focus-visible:ring-0 focus-visible:outline-none rounded-none transition-colors resize-none" />
              </FormControl>
              <FormLabel className="absolute left-0 top-3 text-muted-foreground duration-300 transform -translate-y-0 scale-100 origin-[0] peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:text-primary peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-75 cursor-text">
                Briefly describe your symptoms
              </FormLabel>
              <FormMessage className="absolute -bottom-5 left-0 text-xs" />
            </FormItem>} />
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base uppercase tracking-widest font-bold bg-primary/10 hover:bg-primary/20 text-primary border border-primary/50 hover:border-primary transition-all duration-300 relative overflow-hidden group">
        {isSubmitting ? <span className="flex items-center gap-2">
            <Loader className="w-5 h-5 animate-spin" />
            <span className="animate-pulse">Securing Session...</span>
          </span> : <span className="flex items-center gap-2 relative z-10">
            Secure Your Free Consultation
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>}
        {}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] pointer-events-none" />
      </Button>
    </Form>;
}